<template>
  <div class="animated fadeIn">
    <b-card>
      <h1>Audit Log</h1>

      <b-form-row class="mb-2">
        <b-col>
          <label>
            Search
            <b-input
              v-model="params.query"
              debounce="500"
              placeholder="search..."
              type="search"
            />
          </label>
        </b-col>
        <b-col>
          <label>
            After
            <b-datepicker
              v-model="params.min_date"
              :date-format-options="{
                year: 'numeric',
                month: 'numeric',
                day: 'numeric'
              }"
              :max="params.max_date || new Date()"
              reset-button
            />
          </label>
        </b-col>
        <b-col>
          <label>
            Before
            <b-datepicker
              v-model="params.max_date"
              :date-format-options="{
                year: 'numeric',
                month: 'numeric',
                day: 'numeric'
              }"
              :max="new Date()"
              reset-button
            />
          </label>
        </b-col>
      </b-form-row>

      <div v-if="Object.keys($route.query).length" class="mb-2">
        <b-form-tag
          v-for="(value, key) in $route.query"
          :key="key"
          pill
          @remove="removeRouteQuery(key)"
        >
          {{ key }}: {{ value }}
        </b-form-tag>
      </div>

      <div class="overflow-auto">
        <b-table
          ref="table"
          class="text-nowrap"
          :fields="[
            { key: 'actor', label: 'Actor' },
            { key: 'user', label: 'User' },
            { key: 'action', label: 'Action' },
            { key: 'ip', label: 'IP Address' },
            { key: 'created_at', label: 'Date' }
          ]"
          :items="searchLogs"
          :filter="[
            $route.query,
            params.query,
            params.min_date,
            params.max_date
          ]"
          :current-page="logPage"
          :per-page="20"
          :busy="loading"
          select-mode="single"
          selectable
          show-empty
          hoverable
          @row-clicked="displayDetails"
        >
          <template #table-busy>
            <div class="py-5 text-center">
              <b-spinner />
            </div>
          </template>

          <template #cell(actor)="{ item }">
            <template v-if="item.actor">
              <span v-b-tooltip.hover="item.actor.email">
                {{ item.actor.name }}
              </span>
              <b-link
                v-b-tooltip.hover="'view audit logs'"
                :to="`/admin/audit-log?actor_id=${item.actor.id}`"
                class="ml-2"
              >
                <i class="fa fa-external-link" />
              </b-link>
            </template>
            <span v-else>-</span>
          </template>

          <template #cell(user)="{ item }">
            <template v-if="item.user">
              <span v-b-tooltip.hover="item.user.email">
                {{ item.user.name }}
              </span>
              <b-link
                v-b-tooltip.hover="'view audit logs'"
                :to="`/admin/audit-log?user_id=${item.user.id}`"
                class="ml-2"
              >
                <i class="fa fa-external-link" />
              </b-link>
            </template>
            <span v-else>-</span>
          </template>

          <template #cell(created_at)="{ item }">
            <span v-b-tooltip="new Date(item.created_at).toLocaleString()">
              {{ item.created_at | date }}
            </span>
          </template>
        </b-table>
      </div>
      <b-pagination
        v-if="logTotal > 20"
        v-model="logPage"
        :total-rows="logTotal"
        :per-page="20"
        align="center"
        prev-text="Prev"
        next-text="Next"
        hide-goto-end-buttons
      />
    </b-card>

    <b-modal
      id="log-details"
      centered
      hide-footer
      hide-header
      @hidden="details = null"
    >
      <h2 class="mb-4">Log Details</h2>

      <div v-for="detail of logDetails" :key="detail.label" class="mb-2">
        <div class="font-weight-bold">
          {{ detail.label }}
          <b-link
            v-if="detail.query"
            v-b-tooltip.hover="'view audit logs'"
            :to="`/admin/audit-log?${detail.query}`"
            class="ml-2"
            @click="$bvModal.hide('log-details')"
          >
            <i class="fa fa-external-link" />
          </b-link>
        </div>
        <span v-if="detail.value" style="white-space: pre-wrap">{{
          detail.value
        }}</span>
        <pre v-else-if="detail.pre">{{ detail.pre }}</pre>
      </div>
    </b-modal>
  </div>
</template>

<script>
import API from "@/api";

export default {
  data: () => ({
    details: null,
    loading: false,
    logPage: 1,
    logTotal: 0,
    params: {
      query: "",
      min_date: "",
      max_date: ""
    }
  }),

  computed: {
    logDetails() {
      const { details } = this;
      if (!details) return [];

      let licenseString = "";
      if (details.license) {
        const { type, owner, using } = details.license;
        licenseString = type;
        if (owner) licenseString += `\nowner: ${owner.name} (${owner.email})`;
        if (using) licenseString += `\nusing: ${using.name} (${using.email})`;
      }

      return [
        details.actor && {
          label: "Actor",
          value: `${details.actor.name} (${details.actor.email})`,
          query: `actor_id=${details.actor.id}`
        },
        details.user && {
          label: "User",
          value: `${details.user.name} (${details.user.email})`,
          query: `user_id=${details.user.id}`
        },
        {
          label: "Date",
          value: new Date(details.created_at).toLocaleString()
        },
        details.license && {
          label: "License",
          value: licenseString,
          query: `license_id=${details.license.id}`
        },
        details.license_id &&
          !details.license && {
            label: "License",
            value: `ID ${details.license_id} (deleted)`,
            query: `license_id=${details.license_id}`
          },
        details.organization && {
          label: "Organization",
          value: details.organization.name,
          query: `organization_id=${details.organization.id}`
        },
        details.organization_id &&
          !details.organization && {
            label: "Organization",
            value: `ID ${details.organization_id} (deleted)`,
            query: `organization_id=${details.organization_id}`
          },
        {
          label: "Action",
          value: details.action,
          query: `action=${details.action}`
        },
        {
          label: "IP Address",
          value: details.ip,
          query: `ip=${details.ip}`
        },
        {
          label: "Data",
          pre: JSON.stringify(JSON.parse(details.data), null, 2)
        }
      ].filter(Boolean);
    }
  },

  watch: {
    "$route.query"() {
      this.logPage = 1;
    }
  },

  methods: {
    displayDetails(row) {
      this.details = row;
      this.$refs.table.clearSelected();
      this.$bvModal.show("log-details");
    },

    removeRouteQuery(key) {
      const query = { ...this.$route.query };
      delete query[key];
      this.$router.push({ query });
    },

    async searchLogs(ctx) {
      this.loading = true;
      const params = { ...this.params, ...this.$route.query };
      const { success, results } = await API.adminAuditLogSearch(
        params,
        ctx.currentPage,
        ctx.perPage
      );
      this.loading = false;
      if (!success) return [];

      this.logTotal = results.total;
      return results.results;
    }
  }
};
</script>
